import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../../navigation/routeConfig";
import { BODY_FONT, cardSx, DISPLAY_FONT, INK, INK_SOFT, LINE, PRIMARY } from "../dashboardTokens";

interface WishlistItem {
  id: number;
  drugName?: string;
  ndcNumber?: string;
  strength?: string;
}

interface WishlistQuickViewProps {
  items: WishlistItem[];
  loading?: boolean;
}

export const WishlistQuickView: React.FC<WishlistQuickViewProps> = ({ items, loading }) => {
  const navigate = useNavigate();
  const preview = items.slice(0, 5);

  return (
    <Box sx={{ ...cardSx, p: 2.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 16, color: INK }}>
          Price Alerts
        </Typography>
        <Button
          size="small"
          onClick={() => navigate(routeConfigs.wishlist.path)}
          sx={{ textTransform: "none", fontWeight: 600, color: PRIMARY, fontSize: 12 }}
        >
          Wishlist
        </Button>
      </Box>

      {loading &&
        Array.from({ length: 3 }).map((_, idx) => (
          <Skeleton key={idx} height={40} sx={{ mb: 1 }} />
        ))}

      {!loading && preview.length === 0 && (
        <Typography sx={{ fontFamily: BODY_FONT, fontSize: 13, color: INK_SOFT, py: 1 }}>
          No tracked items yet. Add medications to your wishlist to get alerts.
        </Typography>
      )}

      {!loading &&
        preview.map((item, index) => (
          <Box
            key={item.id}
            onClick={() => navigate(routeConfigs.wishlist.path)}
            sx={{
              py: 1.25,
              borderTop: index === 0 ? "none" : `1px solid ${LINE}`,
              cursor: "pointer",
              "&:hover .drug-name": { color: PRIMARY },
            }}
          >
            <Typography
              className="drug-name"
              sx={{
                fontFamily: BODY_FONT,
                fontWeight: 600,
                fontSize: 13,
                color: INK,
                transition: "color 0.15s ease",
              }}
            >
              {item.drugName || "Untitled medication"}
            </Typography>
            <Typography sx={{ fontFamily: BODY_FONT, fontSize: 12, color: INK_SOFT, mt: 0.25 }}>
              {[item.ndcNumber, item.strength].filter(Boolean).join(" · ") || "No NDC"}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};

export default WishlistQuickView;
